// Port of TrailNavigation/OffRouteDetector.swift.

import type { EpochMs } from '../core/models';
import { NavigationLog } from './navigationLog';

/** Tuning of the detector (Swift `OffRouteDetector.Configuration`). */
export interface OffRouteConfiguration {
  /** When false the detector never fires. */
  isEnabled: boolean;
  /** Cross-track distance (m) beyond which a fix counts as off route (before accuracy padding). */
  thresholdMeters: number;
  /** Cross-track distance (m) below which an off-route hiker counts as back on route. */
  returnThresholdMeters: number;
  /** Consecutive fixes beyond the threshold needed before declaring off route. */
  requiredConsecutiveFixes: number;
  /** Minimum time (seconds) between the first suspicious fix and the off-route declaration. */
  minimumDuration: number;
  /** Fixes with a worse horizontal accuracy (m) are ignored. */
  maxUsableAccuracy: number;
}

const defaultConfiguration: Readonly<OffRouteConfiguration> = Object.freeze({
  isEnabled: true,
  thresholdMeters: 60,
  returnThresholdMeters: 35,
  requiredConsecutiveFixes: 3,
  minimumDuration: 20,
  maxUsableAccuracy: 80,
});

export const OffRouteConfiguration = {
  /** Creates a configuration; omitted fields take the `default` values. */
  make(overrides: Partial<OffRouteConfiguration> = {}): OffRouteConfiguration {
    return { ...defaultConfiguration, ...overrides };
  },
  /** 60 m leave / 35 m return, 3 fixes over ≥ 20 s. */
  default: defaultConfiguration,
  /** 40 m leave / 25 m return, 3 fixes over ≥ 15 s (well-marked paths, good sky view). */
  strict: Object.freeze({
    ...defaultConfiguration,
    thresholdMeters: 40,
    returnThresholdMeters: 25,
    minimumDuration: 15,
  }),
  /** 120 m leave / 70 m return, 3 fixes over ≥ 30 s (approximate geometry, poor reception). */
  relaxed: Object.freeze({
    ...defaultConfiguration,
    thresholdMeters: 120,
    returnThresholdMeters: 70,
    minimumDuration: 30,
  }),
};

/** Detector state. `since` is the time of the first suspicious fix. */
export type OffRouteState =
  | { kind: 'onRoute' }
  | { kind: 'suspected'; since: EpochMs }
  | { kind: 'offRoute'; since: EpochMs };

/** True for the confirmed off-route state. */
export function isOffRouteState(state: OffRouteState): boolean {
  return state.kind === 'offRoute';
}

/** Transition events worth notifying the hiker about. */
export type OffRouteEvent =
  /** Confirmed off route; `distance` is the cross-track distance (m) of the confirming fix. */
  | { kind: 'leftRoute'; distance: number }
  /** Back within the return threshold after having been off route. */
  | { kind: 'returnedToRoute' };

/**
 * Debounced off-route detection with accuracy compensation and return hysteresis.
 *
 * State machine:
 * - `onRoute` → `suspected(since)` on the first usable fix beyond the effective threshold;
 * - `suspected` → `offRoute(since)` (event `leftRoute`) once at least `requiredConsecutiveFixes` consecutive fixes were
 *   beyond the threshold **and** at least `minimumDuration` elapsed since the first of them; any fix within the
 *   threshold clears the suspicion silently (the hiker never "left");
 * - `offRoute` → `onRoute` (event `returnedToRoute`) only when a usable fix is closer than `returnThresholdMeters`,
 *   which is lower than the leave threshold (hysteresis against flapping).
 *
 * Effective leave threshold = `thresholdMeters + min(horizontalAccuracy ?? 0, maxUsableAccuracy) * 0.5`: a vague fix
 * must be proportionally farther away before it counts. The return threshold is deliberately *not* widened. Fixes with
 * accuracy worse than `maxUsableAccuracy` (or invalid, i.e. negative) and fixes not newer than the previous one are
 * ignored and leave the state unchanged. A disabled detector never fires and stays `onRoute`.
 */
export class OffRouteDetector {
  /** The configuration in use. */
  readonly configuration: Readonly<OffRouteConfiguration>;

  private currentState: OffRouteState = { kind: 'onRoute' };
  private consecutiveBeyond = 0;
  private lastEvaluation: EpochMs | undefined;

  /** Creates a detector in the `onRoute` state. */
  constructor(configuration: Readonly<OffRouteConfiguration> = OffRouteConfiguration.default) {
    this.configuration = configuration;
  }

  /** Current state. */
  get state(): OffRouteState {
    return this.currentState;
  }

  /** Effective leave threshold for a fix: `thresholdMeters + min(accuracy ?? 0, maxUsableAccuracy) * 0.5`. */
  effectiveThreshold(horizontalAccuracy: number | undefined): number {
    const accuracy =
      horizontalAccuracy !== undefined && Number.isFinite(horizontalAccuracy) ? Math.max(0, horizontalAccuracy) : 0;
    const config = this.configuration;
    return config.thresholdMeters + Math.min(accuracy, Math.max(0, config.maxUsableAccuracy)) * 0.5;
  }

  /** Feeds one fix's cross-track distance. Returns an event on a confirmed transition. */
  evaluate(
    crossTrackDistance: number,
    horizontalAccuracy: number | undefined,
    date: EpochMs,
  ): OffRouteEvent | undefined {
    const config = this.configuration;
    if (!config.isEnabled) {
      this.currentState = { kind: 'onRoute' };
      this.consecutiveBeyond = 0;
      return undefined;
    }
    if (!Number.isFinite(crossTrackDistance) || crossTrackDistance < 0) return undefined;
    if (horizontalAccuracy !== undefined) {
      const usable = Number.isFinite(horizontalAccuracy) && horizontalAccuracy >= 0;
      if (!usable || horizontalAccuracy > config.maxUsableAccuracy) return undefined;
    }
    if (this.lastEvaluation !== undefined && date <= this.lastEvaluation) return undefined;
    this.lastEvaluation = date;

    const isBeyond = crossTrackDistance > this.effectiveThreshold(horizontalAccuracy);
    const state = this.currentState;
    switch (state.kind) {
      case 'onRoute':
        if (!isBeyond) return undefined;
        this.consecutiveBeyond = 1;
        this.currentState = { kind: 'suspected', since: date };
        return this.confirmIfDue(date, date, crossTrackDistance);
      case 'suspected':
        if (!isBeyond) {
          this.currentState = { kind: 'onRoute' };
          this.consecutiveBeyond = 0;
          return undefined;
        }
        this.consecutiveBeyond += 1;
        return this.confirmIfDue(state.since, date, crossTrackDistance);
      case 'offRoute': {
        const returnThreshold = Math.min(config.returnThresholdMeters, config.thresholdMeters);
        if (!(crossTrackDistance < returnThreshold)) return undefined;
        this.currentState = { kind: 'onRoute' };
        this.consecutiveBeyond = 0;
        NavigationLog.logger.info('Off-route detector: returned to route');
        return { kind: 'returnedToRoute' };
      }
    }
  }

  /** Returns to `onRoute` and forgets history (e.g. after the user changes route). */
  reset(): void {
    this.currentState = { kind: 'onRoute' };
    this.consecutiveBeyond = 0;
    this.lastEvaluation = undefined;
  }

  private confirmIfDue(since: EpochMs, now: EpochMs, distance: number): OffRouteEvent | undefined {
    const requiredFixes = Math.max(1, this.configuration.requiredConsecutiveFixes);
    const requiredDuration = Math.max(0, this.configuration.minimumDuration);
    if (this.consecutiveBeyond < requiredFixes || !((now - since) / 1000 >= requiredDuration)) return undefined;
    this.currentState = { kind: 'offRoute', since };
    NavigationLog.logger.info(`Off-route detector: left route (${Math.trunc(distance)} m)`);
    return { kind: 'leftRoute', distance };
  }
}
